/**
 * Helpers de formatage des dates dans le fuseau horaire de l'entreprise.
 *
 * Les datetimes MySQL sont stockés en UTC sans marqueur ; on force
 * l'interprétation UTC avant tout formatage. Le fuseau d'affichage
 * est celui configuré dans `companies.timezone` (ex. 'Europe/Paris').
 */

const DEFAULT_TZ = 'Europe/Paris';

/**
 * Ajoute le suffixe 'Z' aux datetimes MySQL (`YYYY-MM-DD HH:MM:SS`)
 * pour qu'ils soient interprétés en UTC par `new Date()`.
 */
export function normalizeMysqlUtc(s: string | null | undefined): string {
    if (!s) return '';
    const str = String(s);
    if (/[Zz]$|[+\-]\d{2}:?\d{2}$/.test(str)) return str;
    if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2})?(\.\d+)?$/.test(str)) {
        return str.replace(' ', 'T') + 'Z';
    }
    return str;
}

/** Date + heure complète : ex. "26/04/2026 à 14:32". */
export function formatDateTimeInTZ(value: string | Date | null | undefined, tz?: string): string {
    if (!value) return '';
    const date = value instanceof Date ? value : new Date(normalizeMysqlUtc(String(value)));
    if (isNaN(date.getTime())) return String(value);
    const formatter = new Intl.DateTimeFormat('fr-FR', {
        timeZone: tz || DEFAULT_TZ,
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
    return formatter.format(date).replace(', ', ' à ');
}

/** Heure seule : ex. "14:32". */
export function formatTimeInTZ(value: string | Date | null | undefined, tz?: string): string {
    if (!value) return '';
    const date = value instanceof Date ? value : new Date(normalizeMysqlUtc(String(value)));
    if (isNaN(date.getTime())) return String(value);
    return new Intl.DateTimeFormat('fr-FR', {
        timeZone: tz || DEFAULT_TZ,
        hour: '2-digit',
        minute: '2-digit',
    }).format(date);
}

/** Date seule : ex. "26/04/2026". */
export function formatDateInTZ(value: string | Date | null | undefined, tz?: string): string {
    if (!value) return '';
    const date = value instanceof Date ? value : new Date(normalizeMysqlUtc(String(value)));
    if (isNaN(date.getTime())) return String(value);
    return new Intl.DateTimeFormat('fr-FR', {
        timeZone: tz || DEFAULT_TZ,
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    }).format(date);
}

/**
 * Liste des fuseaux disponibles pour l'UI de paramètres.
 * On limite à un sous-ensemble pertinent pour le SaaS plutôt que l'intégralité IANA.
 */
export const COMMON_TIMEZONES: { value: string; label: string }[] = [
    { value: 'Europe/Paris',    label: 'Europe / Paris (GMT+1/+2)' },
    { value: 'Europe/Brussels', label: 'Europe / Bruxelles (GMT+1/+2)' },
    { value: 'Europe/Madrid',   label: 'Europe / Madrid (GMT+1/+2)' },
    { value: 'Europe/Lisbon',   label: 'Europe / Lisbonne (GMT/+1)' },
    { value: 'Europe/London',   label: 'Europe / Londres (GMT/+1)' },
    { value: 'Europe/Zurich',   label: 'Europe / Zurich (GMT+1/+2)' },
    { value: 'Europe/Rome',     label: 'Europe / Rome (GMT+1/+2)' },
    { value: 'Europe/Athens',   label: 'Europe / Athènes (GMT+2/+3)' },
    { value: 'Africa/Casablanca', label: 'Afrique / Casablanca (GMT+1)' },
    { value: 'Africa/Algiers',  label: 'Afrique / Alger (GMT+1)' },
    { value: 'Africa/Tunis',    label: 'Afrique / Tunis (GMT+1)' },
    { value: 'America/New_York', label: 'Amérique / New York (GMT-5/-4)' },
    { value: 'America/Los_Angeles', label: 'Amérique / Los Angeles (GMT-8/-7)' },
    { value: 'America/Montreal', label: 'Amérique / Montréal (GMT-5/-4)' },
    { value: 'Asia/Dubai',      label: 'Asie / Dubaï (GMT+4)' },
    { value: 'Asia/Tokyo',      label: 'Asie / Tokyo (GMT+9)' },
    { value: 'Australia/Sydney', label: 'Australie / Sydney (GMT+10/+11)' },
    { value: 'UTC',             label: 'UTC (GMT)' },
];
